import type { AssetFacets, Audience, DocumentType, FacetCount } from "@/api/client";
import { AUDIENCE_LABEL, DOCUMENT_TYPE_PLURAL } from "@/lib/asset-labels";
import { cn } from "@/lib/utils";

export interface AssetFilterState {
  /** null = "Show All". Single-select (the mockup's audience row). */
  audience: Audience | null;
  /** Multi-select within the row (OR); AND across rows. */
  documentTypes: DocumentType[];
  /** Selected topic term IDs (stable UUIDs), multi-select. Keyed by ID — not
   * slug — so a renamed tag (slug changes, ID is stable) follows the rename and
   * stays selected; only a tag removed from every asset (gone from the facets)
   * is pruned. The route resolves each ID to its current slug for the `topic=`
   * query param (CHP-431). */
  topicIds: string[];
  /** CHP-1035: selected industry term IDs — same ID-keyed contract as
   * `topicIds` (resolved to slugs for the `industry=` query param). */
  industryIds: string[];
}

interface Props {
  facets: AssetFacets;
  total: number;
  state: AssetFilterState;
  onChange: (next: AssetFilterState) => void;
}

function Pill({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-sm transition-colors",
        active
          ? "border-primary bg-brand-tint text-primary"
          : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {label}
      {count != null ? <span className="ml-1 text-xs opacity-70">({count})</span> : null}
    </button>
  );
}

/** The "Browse by category" rows: audience / document type / topic / industry.
 * Counts are bracket totals from the server facets. Zero-count options are
 * hidden so the rows never present a dead-end pill (CHP-384a), and every row
 * carries a "Show All" reset for consistency (CHP-373). */
export function AssetFilters({ facets, total, state, onChange }: Props) {
  const toggleInList = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const docCount = (key: string) => facets.document_type.find((f) => f.key === key)?.count ?? 0;
  const audienceCount = (key: string) => facets.audience.find((f) => f.key === key)?.count ?? 0;

  // CHP-431: render only the live facet topics, and key selection by the
  // stable term ID (not the slug). A rename changes the slug but keeps the ID,
  // so the selection follows the rename in-place (the pill just shows the new
  // label); a tag removed from every asset drops out of the facets entirely and
  // is pruned from the selection in the route (see assets.tsx). Either way no
  // stale/blank pill can linger here. Supersedes the CHP-384b orphan-pill
  // approach, which kept a manually-deselectable pill for a vanished topic (it
  // could lock selected — UAT Flow E).
  const topicPills = facets.topics;
  // CHP-1035: the Industry row mirrors Topic — same taxonomy facet contract.
  const industryPills = facets.industries;

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <Row label="Audience">
        <Pill
          active={state.audience === null}
          label="Show All"
          count={total}
          onClick={() => onChange({ ...state, audience: null })}
        />
        {(["my_learning", "for_clients"] as Audience[])
          .filter((aud) => audienceCount(aud) > 0)
          .map((aud) => (
            <Pill
              key={aud}
              active={state.audience === aud}
              label={AUDIENCE_LABEL[aud]}
              count={audienceCount(aud)}
              onClick={() => onChange({ ...state, audience: state.audience === aud ? null : aud })}
            />
          ))}
      </Row>

      <Row label="Document type">
        <Pill
          active={state.documentTypes.length === 0}
          label="Show All"
          count={total}
          onClick={() => onChange({ ...state, documentTypes: [] })}
        />
        {(["one_pager", "battlecard", "slide_deck", "case_study"] as DocumentType[])
          .filter((dt) => docCount(dt) > 0)
          .map((dt) => (
            <Pill
              key={dt}
              active={state.documentTypes.includes(dt)}
              label={DOCUMENT_TYPE_PLURAL[dt]}
              count={docCount(dt)}
              onClick={() =>
                onChange({ ...state, documentTypes: toggleInList(state.documentTypes, dt) })
              }
            />
          ))}
      </Row>

      {topicPills.length > 0 ? (
        <Row label="Topic">
          <Pill
            active={state.topicIds.length === 0}
            label="Show All"
            count={total}
            onClick={() => onChange({ ...state, topicIds: [] })}
          />
          {topicPills.map((t: FacetCount) => {
            // Taxonomy facets always carry a non-null term ID; the guard keeps
            // TypeScript happy and skips any malformed facet defensively.
            const id = t.id;
            if (id == null) return null;
            return (
              <Pill
                key={t.key}
                active={state.topicIds.includes(id)}
                label={t.label}
                count={t.count}
                onClick={() => onChange({ ...state, topicIds: toggleInList(state.topicIds, id) })}
              />
            );
          })}
        </Row>
      ) : null}

      {industryPills.length > 0 ? (
        <Row label="Industry">
          <Pill
            active={state.industryIds.length === 0}
            label="Show All"
            count={total}
            onClick={() => onChange({ ...state, industryIds: [] })}
          />
          {industryPills.map((t: FacetCount) => {
            const id = t.id;
            if (id == null) return null;
            return (
              <Pill
                key={t.key}
                active={state.industryIds.includes(id)}
                label={t.label}
                count={t.count}
                onClick={() =>
                  onChange({ ...state, industryIds: toggleInList(state.industryIds, id) })
                }
              />
            );
          })}
        </Row>
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-28 flex-shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}
